import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useAppDispatch } from '../../../../app/hooks';
import { FTAnswer, MCAnswer, NewQuestionModalProps } from './types';
import {
  createFTAnswerAsync,
  createMCAnswerAsync,
  createQuestionAsync,
  destroyFTAnswerAsync,
  destroyMCAnswerAsync,
  fetchFTAnswerAsync,
  fetchImageAsync,
  fetchMCAnswersAsync,
  updateFTAnswerAsync,
  updateMCAnswerAsync,
  updateQuestionAsync
} from './questionSlice';
import { QuestionsCreateRequest } from '../../../../generated/api';
import { AddPhotoAlternate, Delete } from '@mui/icons-material';
import { setNotification } from '../../../../app/appSlice';

const QuestionDialog = ({ open, close, category, question }: NewQuestionModalProps) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [type, setType] = useState<{ label: string; value: string } | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [sortIndex, setSortIndex] = useState<number>(1);
  const [mcAnswers, setMcAnswers] = useState<MCAnswer[]>([]);
  const [ftAnswer, setFtAnswer] = useState<FTAnswer>({ correctAnswer: '' });
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const typeOptions = [
    { label: 'Multiple choice question', value: 'MC' },
    { label: 'Free text question', value: 'FT' }
  ];

  useEffect(() => {
    if (!question) return;
    setName(question.name);
    setDescription(question.description);
    setSortIndex(question.sortIndex);

    setType(typeOptions.find((t) => t.value === question.type) ?? null);
    if (question.image) {
      dispatch(fetchImageAsync(question.id))
        .unwrap()
        .then((value) => setImage(new File([value], `${question.image?.split('/').at(-1)}`)));
    }
    if (question.type === 'MC') {
      dispatch(fetchMCAnswersAsync({ questionId: question.id.toString() }))
        .unwrap()
        .then((value) => setMcAnswers(value));
    }
    if (question.type === 'FT') {
      dispatch(fetchFTAnswerAsync({ questionId: question.id.toString() }))
        .unwrap()
        .then((value) => {
          if (value.length > 0) setFtAnswer(value[0]);
        });
    }
  }, [question]);

  useEffect(() => {
    if (image) {
      setImageUrl(URL.createObjectURL(image));
    } else {
      setImageUrl(null);
    }
  }, [image]);

  const handleSubmit = async () => {
    if (!type || !name) {
      setShowErrors(true);
      return;
    }
    const data: QuestionsCreateRequest = {
      name,
      description,
      type: type.value,
      category: category.id,
      image: image ?? new File([], ''),
      sortIndex: sortIndex
    };
    try {
      const response = await dispatch(
        question ? updateQuestionAsync({ ...data, id: question.id }) : createQuestionAsync(data)
      ).unwrap();
      if (type.value === 'MC') {
        if (ftAnswer.id) dispatch(destroyFTAnswerAsync(ftAnswer.id));
        for (const option of mcAnswers) {
          if (option.removed && option.id) {
            dispatch(destroyMCAnswerAsync(option.id));
            continue;
          }
          if (option.id) {
            await dispatch(updateMCAnswerAsync({ id: option.id, data: { ...option, question: response.id } })).unwrap();
            continue;
          }
          await dispatch(createMCAnswerAsync({ ...option, question: response.id })).unwrap();
        }
      }
      if (type.value === 'FT') {
        mcAnswers.map((a) => {
          if (a.id) dispatch(destroyMCAnswerAsync(a.id));
        });
        if (ftAnswer.id) {
          await dispatch(
            updateFTAnswerAsync({ id: ftAnswer.id, data: { ...ftAnswer, question: response.id } })
          ).unwrap();
        } else {
          await dispatch(createFTAnswerAsync({ ...ftAnswer, question: response.id })).unwrap();
        }
      }
    } catch (e) {
      dispatch(setNotification({ message: String(e), type: 'error' }));
      setShowErrors(true);
      return;
    }
    handleClose();
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setType(null);
    setImage(null);
    setImageUrl(null);
    setMcAnswers([]);
    setFtAnswer({ correctAnswer: '' });
    setShowErrors(false);
    setSortIndex(1);
    close();
  };

  const answersCard = () => {
    const removeMCAnswer = (index: number) => {
      const id = mcAnswers[index].id;
      if (id) {
        setMcAnswers((prevState) => prevState.map((a, i) => (i === index ? { ...a, removed: true } : a)));
      } else {
        setMcAnswers((prevState) => prevState.filter((a, i) => i !== index));
      }
    };

    const updateMcText = (index: number, value: string) => {
      setMcAnswers((prevState) => {
        return prevState.map((option, i) => (i === index ? { ...option, text: value } : option));
      });
    };

    const updateMcCorrect = (index: number, value: boolean) => {
      setMcAnswers((prevState) => {
        return prevState.map((option, i) => (i === index ? { ...option, correct: value } : option));
      });
    };

    const updateFTAnswer = (value: string) => {
      setFtAnswer((prevState) => {
        return { ...prevState, correctAnswer: value };
      });
    };

    if (type?.value === 'MC') {
      return (
        <Box>
          <Typography>Options</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>text</TableCell>
                  <TableCell align="center">correct answer?</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {mcAnswers.map((option, index) => {
                  if (option.removed) return null;
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          fullWidth
                          value={option.text}
                          onChange={({ target }) => updateMcText(index, target.value)}
                          error={showErrors && !option.text}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={option.correct}
                          onChange={({ target }) => updateMcCorrect(index, target.checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Button onClick={() => removeMCAnswer(index)}>remove</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Button onClick={() => setMcAnswers((prevState) => [...prevState, { text: '', correct: false }])}>
            Add new Option
          </Button>
        </Box>
      );
    }
    if (type?.value === 'FT') {
      return (
        <Box>
          <TextField
            fullWidth
            label="Correct Answer"
            value={ftAnswer.correctAnswer}
            onChange={({ target }) => updateFTAnswer(target.value)}
          />
        </Box>
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogContent sx={{ minWidth: 500 }}>
        <Box display="flex" justifyContent="space-between" flexDirection="row" alignItems="center">
          <Typography variant="h5">{question ? 'Edit Question' : 'New Question'}</Typography>
          <TextField
            type="number"
            label="Sort Index"
            sx={{ maxWidth: 100 }}
            value={sortIndex}
            onChange={({ target }) => setSortIndex(parseInt(target.value))}
          />
        </Box>
        <Stack spacing={2} marginTop={2}>
          <TextField
            label="Name"
            value={name}
            onChange={({ target }) => setName(target.value)}
            error={showErrors && !name}
          />
          <IconButton component="label">
            <input
              hidden
              accept="image/*"
              type="file"
              onChange={({ target }) => setImage(target.files ? target.files[0] : null)}
            />
            <AddPhotoAlternate />
          </IconButton>
          {imageUrl && (
            <>
              <img src={imageUrl} alt={image?.name} />{' '}
              <Button onClick={() => setImage(null)}>
                <Delete />
              </Button>
            </>
          )}
          <TextField
            multiline
            label="Description"
            value={description}
            onChange={({ target }) => setDescription(target.value)}
          />
          <Autocomplete
            renderInput={(params) => <TextField {...params} label="Type" error={showErrors && !type} />}
            value={type}
            options={typeOptions}
            onChange={(e, option) => setType(option ?? null)}
            isOptionEqualToValue={(option, value) => option.value === value.value}
          />
          {answersCard()}
          <Box display="flex" justifyContent="space-between">
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              Submit
            </Button>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default QuestionDialog;
